import { useCallback, useMemo, useRef, useState } from "react";
import { AskStatus, PLACEHOLDER_ASSISTANT_ID, PLACEHOLDER_USER_ID, } from "@f2e/shared/types";
import { createCodeerV2ApiClient } from "../api/codeer";
import { ConversationErrorType, } from "../types/chat";
import { useSSEClient } from "./api/useSSEClient";
const fileUuidsFromContext = (files) => {
    const uuids = (files ?? [])
        .map((file) => file.uuid)
        .filter((uuid) => Boolean(uuid));
    return uuids.length > 0 ? uuids : undefined;
};
const resolveV2BaseUrl = (baseUrl) => baseUrl.replace(/\/api\/v1\/?$/, "/api/v2");
const nowIso = () => new Date().toISOString();
const makeLocalPart = ({ id, historyId, groupId, sequence, partKind, content, attachedFiles, }) => ({
    id,
    history_id: historyId,
    conversation_id: null,
    conversation_group_id: groupId,
    sequence,
    part_kind: partKind,
    content,
    metadata: { source: "local-placeholder" },
    source: "stack",
    attached_files: attachedFiles?.map((file) => ({
        id: file.uuid || file.id,
        type: file.type,
        name: file.name,
        url: file.url,
        scope: file.scope,
        attachment_type: file.attachmentType,
    })),
    created_at: nowIso(),
    updated_at: nowIso(),
});
const upsertPart = (parts, incoming) => {
    const index = parts.findIndex((part) => {
        if (incoming.id != null && part.id === incoming.id)
            return true;
        if (incoming.part_kind !== part.part_kind)
            return false;
        if (incoming.conversation_group_id !== part.conversation_group_id) {
            return false;
        }
        const incomingCallId = incoming.content.tool_call_id;
        const partCallId = part.content.tool_call_id;
        return (typeof incomingCallId === "string" &&
            incomingCallId.length > 0 &&
            incomingCallId === partCallId);
    });
    if (index === -1) {
        return [...parts, incoming];
    }
    const next = [...parts];
    next[index] = incoming;
    return next;
};
const getRequestFormInteraction = (part) => {
    const interaction = part.metadata?.interaction;
    return interaction?.type === "request_form" ? interaction : undefined;
};
const getLegacyConversationId = (part) => {
    if (typeof part.conversation_id === "number" &&
        Number.isFinite(part.conversation_id) &&
        part.conversation_id > 0) {
        return part.conversation_id;
    }
    const legacyConversationId = part.metadata.legacy_conversation_id;
    return typeof legacyConversationId === "number" &&
        Number.isFinite(legacyConversationId) &&
        legacyConversationId > 0
        ? legacyConversationId
        : undefined;
};
export const useChatSessionV2 = (options) => {
    const getBaseUrlRef = useRef(options.getBaseUrl);
    const getAuthHeadersRef = useRef(options.getAuthHeaders);
    const onUnauthorizedRef = useRef(options.onUnauthorized);
    const getStreamMetadataRef = useRef(options.getStreamMetadata);
    const activeHistoryAnalysisRef = useRef(null);
    getBaseUrlRef.current = options.getBaseUrl;
    getAuthHeadersRef.current = options.getAuthHeaders;
    onUnauthorizedRef.current = options.onUnauthorized;
    getStreamMetadataRef.current = options.getStreamMetadata;
    const [history, setHistory] = useState();
    const [context, setContext] = useState();
    const contextRef = useRef(undefined);
    const [parts, setParts] = useState([]);
    const [replyContextAnchor, setReplyContextAnchor] = useState(null);
    const [loadConversationsError, setLoadConversationsError] = useState();
    const [askingStatus, setAskingStatus] = useState(AskStatus.Idle);
    const askingStatusRef = useRef(AskStatus.Idle);
    const historyIdRef = useRef(undefined);
    const partsRequestIdRef = useRef(0);
    const localGroupRef = useRef(undefined);
    const [submittingFormRequestId, setSubmittingFormRequestId] = useState(null);
    const [pendingPayment, setPendingPayment] = useState(null);
    const getV2BaseUrl = useCallback(() => resolveV2BaseUrl(getBaseUrlRef.current()), []);
    const api = useMemo(() => createCodeerV2ApiClient({
        getBaseUrl: getV2BaseUrl,
        getAuthHeaders: () => getAuthHeadersRef.current?.() ?? {},
        onUnauthorized: () => onUnauthorizedRef.current?.(),
        conversationLimit: options.conversationLimit ?? 301,
        messageWindow: options.messageWindow ?? "earliest",
    }), [getV2BaseUrl, options.conversationLimit, options.messageWindow]);
    const client = useSSEClient({
        getBaseUrl: getV2BaseUrl,
        getAuthHeaders: () => getAuthHeadersRef.current?.() ?? {},
        onUnauthorized: () => onUnauthorizedRef.current?.(),
        createChatPath: "/chats",
        chatPathPrefix: "/chats",
        contractVersion: "v2",
        includeToolTags: false,
    });
    const setAsking = useCallback((status) => {
        askingStatusRef.current = status;
        setAskingStatus(status);
    }, []);
    const refreshConversations = useCallback(async (opts) => {
        const historyId = historyIdRef.current;
        if (!historyId || historyId <= 0)
            return;
        const requestId = ++partsRequestIdRef.current;
        const isCurrentRequest = () => partsRequestIdRef.current === requestId &&
            historyIdRef.current === historyId;
        try {
            const nextData = await api.fetchPartsData(historyId);
            if (!isCurrentRequest())
                return;
            setParts(nextData.messages);
            setReplyContextAnchor(nextData.reply_context_anchor ?? null);
            setLoadConversationsError(undefined);
            return nextData.messages;
        }
        catch (error) {
            if (!isCurrentRequest())
                return;
            if (opts?.silent) {
                throw error;
            }
            setLoadConversationsError(error);
            options.onError?.(ConversationErrorType.RefreshConversationFailed);
        }
    }, [api, options]);
    const initConversation = useCallback((initContext) => {
        const initialHistory = initContext.chatSession ??
            {
                id: -1,
                name: initContext.question,
                createdAt: nowIso(),
                updatedAt: nowIso(),
                workspace: initContext.workspace
                    ? {
                        id: initContext.workspace.id,
                        name: initContext.workspace.name,
                        default: false,
                        createdAt: "",
                        updatedAt: "",
                    }
                    : {
                        id: "",
                        name: "",
                        default: false,
                        createdAt: "",
                        updatedAt: "",
                    },
            };
        historyIdRef.current =
            initialHistory.id > 0 ? initialHistory.id : undefined;
        partsRequestIdRef.current += 1;
        setHistory(initialHistory);
        setContext(initContext);
        contextRef.current = initContext;
        setParts([]);
        setReplyContextAnchor(null);
        setAsking(AskStatus.Init);
    }, [setAsking]);
    const loadConversations = useCallback(async (targetHistory) => {
        const requestId = ++partsRequestIdRef.current;
        const isCurrentRequest = () => partsRequestIdRef.current === requestId;
        setLoadConversationsError(undefined);
        try {
            const nextData = await api.fetchPartsData(targetHistory.id);
            if (!isCurrentRequest())
                return;
            const nextContext = {
                chatSession: targetHistory,
                question: "",
                type: contextRef.current?.type ?? "conversation",
            };
            historyIdRef.current = targetHistory.id;
            setHistory(targetHistory);
            setContext(nextContext);
            contextRef.current = nextContext;
            setParts(nextData.messages);
            setReplyContextAnchor(nextData.reply_context_anchor ?? null);
            return nextData.messages;
        }
        catch (error) {
            if (!isCurrentRequest())
                return;
            setLoadConversationsError(error);
            throw error;
        }
    }, [api]);
    const ensureHistory = useCallback(async (message, sendOptions) => {
        const existingHistory = history && history.id > 0
            ? history
            : contextRef.current?.chatSession &&
                contextRef.current.chatSession.id > 0
                ? contextRef.current.chatSession
                : undefined;
        if (existingHistory) {
            historyIdRef.current = existingHistory.id;
            return existingHistory;
        }
        const streamMetadata = options.getStreamMetadata?.();
        const created = await client.createChat({
            name: message.trim() || "Untitled",
            agentId: sendOptions?.agentId ?? contextRef.current?.settings?.agent?.id,
            mode: streamMetadata?.mode,
            currentPageUrl: sendOptions?.currentPageUrl ?? streamMetadata?.currentPageUrl,
            viewingWorkspaceId: streamMetadata?.viewingWorkspaceId,
            userLocale: streamMetadata?.userLocale,
        });
        historyIdRef.current = created.id;
        setHistory((prev) => prev
            ? {
                ...prev,
                id: created.id,
                name: created.name,
                createdAt: created.createdAt,
                updatedAt: created.updatedAt,
                externalUserId: created.externalUserId,
            }
            : created);
        options.onHistoryIdReceived?.(created.id);
        return created;
    }, [client, history, options]);
    const patchLocalGroup = useCallback((groupId) => {
        const localGroupId = localGroupRef.current;
        if (!localGroupId || localGroupId === groupId)
            return;
        setParts((prev) => prev.map((part) => part.conversation_group_id === localGroupId
            ? { ...part, conversation_group_id: groupId }
            : part));
        localGroupRef.current = groupId;
    }, []);
    const replaceLocalPart = useCallback((localId, incoming) => {
        setParts((prev) => {
            const localIndex = prev.findIndex((part) => part.id === localId);
            if (localIndex === -1) {
                return upsertPart(prev, incoming);
            }
            if (localId === PLACEHOLDER_ASSISTANT_ID) {
                return upsertPart(prev.filter((part) => part.id !== localId), incoming);
            }
            return prev.map((part, index) => index === localIndex ? incoming : part);
        });
    }, []);
    const appendTextDelta = useCallback((chunk) => {
        if (!chunk)
            return;
        setParts((prev) => prev.map((part) => part.id === PLACEHOLDER_ASSISTANT_ID
            ? {
                ...part,
                content: {
                    ...part.content,
                    content: `${String(part.content.content ?? "")}${chunk}`,
                },
            }
            : part));
    }, []);
    const handleInteractionRequest = useCallback((data) => {
        setSubmittingFormRequestId(null);
        if (data.interactionType === "form") {
            setPendingPayment(null);
            return;
        }
        setPendingPayment(data);
    }, []);
    const handlePendingInteractionResolved = useCallback((resolutions) => {
        if (resolutions.some((resolution) => resolution.resolved)) {
            setSubmittingFormRequestId(null);
            setPendingPayment(null);
        }
    }, []);
    const buildCallbacks = useCallback((historyId, sendOptions, lifecycle, onStreamError) => ({
        onCreated: ({ conversationGroupId }) => {
            if (conversationGroupId) {
                patchLocalGroup(conversationGroupId);
                setContext((prev) => {
                    const next = prev ? { ...prev, groupId: conversationGroupId } : prev;
                    contextRef.current = next;
                    return next;
                });
            }
        },
        onPartCreated: (part) => {
            if (part.conversation_group_id) {
                patchLocalGroup(part.conversation_group_id);
            }
            if (part.part_kind === "user-prompt") {
                replaceLocalPart(PLACEHOLDER_USER_ID, part);
                if (part.id !== null &&
                    part.id > 0 &&
                    lifecycle &&
                    !lifecycle.userPromptAccepted) {
                    lifecycle.userPromptAccepted = true;
                    sendOptions?.onUserPromptAccepted?.(part);
                }
                return;
            }
            setParts((prev) => upsertPart(prev, part));
        },
        onPartCompleted: (part) => {
            if (part.part_kind === "text") {
                replaceLocalPart(PLACEHOLDER_ASSISTANT_ID, part);
                return;
            }
            setParts((prev) => upsertPart(prev, part));
        },
        onMessage: appendTextDelta,
        onChatTitleUpdated: (chatId, name) => {
            options.onChatTitleUpdated?.(chatId, name);
            setHistory((prev) => prev && prev.id === chatId ? { ...prev, name } : prev);
        },
        onPendingInteractionResolved: handlePendingInteractionResolved,
        onInteractionRequest: handleInteractionRequest,
        onHistoryAnalysisProgress: ({ runId, workspaceId, agentId }) => {
            activeHistoryAnalysisRef.current = { runId, workspaceId, agentId };
        },
        onDone: () => {
            activeHistoryAnalysisRef.current = null;
            setAsking(AskStatus.Idle);
            void refreshConversations();
            options.onComplete?.(historyId);
        },
        onError: (error) => {
            activeHistoryAnalysisRef.current = null;
            onStreamError?.();
            setAsking(AskStatus.Error);
            setSubmittingFormRequestId(null);
            setPendingPayment(null);
            void refreshConversations();
            if (lifecycle && !lifecycle.errorReported) {
                lifecycle.errorReported = true;
                sendOptions?.onError?.(error);
            }
            options.onError?.(ConversationErrorType.SSEError, error.message);
        },
    }), [
        appendTextDelta,
        handleInteractionRequest,
        handlePendingInteractionResolved,
        options,
        patchLocalGroup,
        refreshConversations,
        replaceLocalPart,
        setAsking,
    ]);
    const sendMessage = useCallback(async (message, sendOptions) => {
        if (askingStatusRef.current === AskStatus.Answering)
            return;
        const trimmedMessage = message.trim();
        const attachedFileUuids = sendOptions === undefined
            ? (contextRef.current?.attachedFileUuids ??
                fileUuidsFromContext(contextRef.current?.files))
            : (sendOptions.attachedFileUuids ??
                fileUuidsFromContext(sendOptions.attachedFiles));
        const attachedFiles = sendOptions === undefined
            ? contextRef.current?.files
            : sendOptions.attachedFiles;
        if (!trimmedMessage && !attachedFileUuids?.length)
            return;
        const lifecycle = {
            userPromptAccepted: false,
            errorReported: false,
        };
        let targetHistory;
        try {
            targetHistory = await ensureHistory(trimmedMessage, sendOptions);
        }
        catch (error) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            lifecycle.errorReported = true;
            sendOptions?.onError?.(normalizedError);
            throw error;
        }
        const streamMetadata = getStreamMetadataRef.current?.();
        const localGroupId = `local-${Date.now()}`;
        localGroupRef.current = localGroupId;
        setAsking(AskStatus.Answering);
        setSubmittingFormRequestId(null);
        setPendingPayment(null);
        setParts((prev) => [
            ...prev,
            makeLocalPart({
                id: PLACEHOLDER_USER_ID,
                historyId: targetHistory.id,
                groupId: localGroupId,
                sequence: prev.length + 1,
                partKind: "user-prompt",
                content: { content: trimmedMessage },
                attachedFiles,
            }),
            makeLocalPart({
                id: PLACEHOLDER_ASSISTANT_ID,
                historyId: targetHistory.id,
                groupId: localGroupId,
                sequence: prev.length + 2,
                partKind: "text",
                content: { content: "" },
            }),
        ]);
        try {
            await client.sendMessage(targetHistory.id, {
                message: trimmedMessage,
                stream: true,
                agentId: sendOptions?.agentId ?? contextRef.current?.settings?.agent?.id,
                agentHistoryId: sendOptions?.agentHistoryId ??
                    contextRef.current?.settings?.agentHistoryId,
                attachedFileUuids,
                mode: streamMetadata?.mode,
                currentPageUrl: sendOptions?.currentPageUrl ?? streamMetadata?.currentPageUrl,
                viewingWorkspaceId: streamMetadata?.viewingWorkspaceId,
                userLocale: streamMetadata?.userLocale,
                imageEdit: sendOptions?.imageEdit,
            }, buildCallbacks(targetHistory.id, sendOptions, lifecycle));
        }
        catch (error) {
            setAsking(AskStatus.Error);
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            if (!lifecycle.errorReported) {
                lifecycle.errorReported = true;
                sendOptions?.onError?.(normalizedError);
            }
            options.onError?.(ConversationErrorType.SSEError, normalizedError.message);
        }
    }, [buildCallbacks, client, ensureHistory, options, setAsking]);
    const regenerate = useCallback(async (conversationGroupId) => {
        const historyId = historyIdRef.current;
        if (!historyId || askingStatusRef.current === AskStatus.Answering)
            return;
        localGroupRef.current = conversationGroupId;
        setAsking(AskStatus.Answering);
        let previousParts = [];
        setParts((prev) => {
            previousParts = prev;
            const targetIndex = prev.findIndex((part) => part.conversation_group_id === conversationGroupId &&
                part.part_kind === "user-prompt");
            const truncated = targetIndex === -1 ? prev : prev.slice(0, targetIndex);
            const questionPart = targetIndex === -1 ? undefined : prev[targetIndex];
            const localQuestionPart = questionPart
                ? {
                    ...questionPart,
                    id: PLACEHOLDER_USER_ID,
                    metadata: {
                        ...questionPart.metadata,
                        source: "local-placeholder",
                    },
                }
                : undefined;
            return [
                ...truncated,
                ...(localQuestionPart ? [localQuestionPart] : []),
                makeLocalPart({
                    id: PLACEHOLDER_ASSISTANT_ID,
                    historyId,
                    groupId: conversationGroupId,
                    sequence: prev.length + 1,
                    partKind: "text",
                    content: { content: "" },
                }),
            ];
        });
        try {
            const streamMetadata = getStreamMetadataRef.current?.();
            await client.regenerateMessage(historyId, {
                conversationGroupId,
                stream: true,
                mode: streamMetadata?.mode,
                currentPageUrl: streamMetadata?.currentPageUrl,
                viewingWorkspaceId: streamMetadata?.viewingWorkspaceId,
                userLocale: streamMetadata?.userLocale,
            }, buildCallbacks(historyId, undefined, undefined, () => {
                setParts(previousParts);
            }));
        }
        catch (error) {
            setParts(previousParts);
            setAsking(AskStatus.Error);
            options.onError?.(ConversationErrorType.SSEError, error instanceof Error ? error.message : String(error));
        }
    }, [buildCallbacks, client, options, setAsking]);
    const cancelHistoryAnalysis = useCallback(async (target) => {
        const authHeaders = await getAuthHeadersRef.current?.();
        const response = await fetch(`${getBaseUrlRef.current()}/history-analysis/workspaces/${target.workspaceId}/agents/${target.agentId}/runs/${target.runId}/cancel`, {
            method: "POST",
            headers: authHeaders,
            credentials: "include",
        });
        if (!response.ok) {
            throw new Error(`Failed to cancel history analysis (${response.status})`);
        }
        const payload = (await response.json());
        if (payload.data?.status !== "cancelled")
            return;
        setParts((previousParts) => previousParts.map((part) => {
            const analysis = part.metadata.history_analysis;
            if (!analysis ||
                typeof analysis !== "object" ||
                analysis.run_id !== target.runId) {
                return part;
            }
            return {
                ...part,
                metadata: {
                    ...part.metadata,
                    history_analysis: {
                        ...analysis,
                        status: "cancelled",
                    },
                },
            };
        }));
        if (activeHistoryAnalysisRef.current?.runId === target.runId) {
            activeHistoryAnalysisRef.current = null;
            client.cancelRequest();
            setAsking(AskStatus.Idle);
        }
    }, [client, setAsking]);
    const cancel = useCallback(() => {
        const activeRun = activeHistoryAnalysisRef.current;
        if (!activeRun) {
            client.cancelRequest();
            setAsking(AskStatus.Idle);
            return;
        }
        void cancelHistoryAnalysis(activeRun).catch((error) => {
            console.error("Failed to cancel active history analysis", error);
        });
    }, [cancelHistoryAnalysis, client, setAsking]);
    const submitFeedback = useCallback(async (target, type, content) => {
        const historyId = historyIdRef.current;
        const targetPart = typeof target === "object" ? target : undefined;
        const partId = typeof target === "number" ? target : targetPart?.id;
        const legacyConversationId = targetPart
            ? getLegacyConversationId(targetPart)
            : undefined;
        if (!historyId ||
            (!partId && !legacyConversationId) ||
            (partId != null && partId <= 0 && !legacyConversationId)) {
            return false;
        }
        const success = await api.createPartFeedback({
            chatId: historyId,
            partId,
            legacyConversationId,
            type,
            content,
        });
        if (success) {
            setParts((prev) => prev.map((part) => {
                const isTarget = partId != null && partId > 0
                    ? part.id === partId
                    : legacyConversationId != null &&
                        getLegacyConversationId(part) === legacyConversationId;
                if (!isTarget)
                    return part;
                const feedbackCounts = { ...(part.feedback_counts ?? {}) };
                feedbackCounts[type] = Math.max(feedbackCounts[type] ?? 0, 1);
                return {
                    ...part,
                    feedback_counts: feedbackCounts,
                };
            }));
            void refreshConversations();
        }
        return success;
    }, [api, refreshConversations]);
    const submitFormInteraction = useCallback(async (part, submission) => {
        const interaction = getRequestFormInteraction(part);
        if (!interaction || interaction.status !== "pending")
            return;
        try {
            setSubmittingFormRequestId(interaction.form_request_id);
            const success = await api.submitFormRequest({
                chatId: part.history_id,
                formRequestId: interaction.form_request_id,
                conversationGroupId: part.conversation_group_id,
                submission,
            });
            if (!success) {
                setSubmittingFormRequestId(null);
                return;
            }
            setSubmittingFormRequestId(null);
            setPendingPayment(null);
            localGroupRef.current = part.conversation_group_id;
            setAsking(AskStatus.Answering);
            await refreshConversations();
            setParts((prev) => {
                const nextSequence = Math.max(0, ...prev.map((item) => typeof item.sequence === "number" ? item.sequence : 0)) + 1;
                return [
                    ...prev.filter((item) => item.id !== PLACEHOLDER_ASSISTANT_ID),
                    makeLocalPart({
                        id: PLACEHOLDER_ASSISTANT_ID,
                        historyId: part.history_id,
                        groupId: part.conversation_group_id,
                        sequence: nextSequence,
                        partKind: "text",
                        content: { content: "" },
                    }),
                ];
            });
            const streamMetadata = getStreamMetadataRef.current?.();
            await client.sendMessage(part.history_id, {
                message: "",
                stream: true,
                agentId: contextRef.current?.settings?.agent?.id,
                agentHistoryId: contextRef.current?.settings?.agentHistoryId,
                resumeFormRequestId: interaction.form_request_id,
                mode: streamMetadata?.mode,
                currentPageUrl: streamMetadata?.currentPageUrl,
                viewingWorkspaceId: streamMetadata?.viewingWorkspaceId,
                userLocale: streamMetadata?.userLocale,
            }, buildCallbacks(part.history_id));
        }
        catch (error) {
            setAsking(AskStatus.Error);
            setSubmittingFormRequestId(null);
            options.onError?.(ConversationErrorType.SSEError, error instanceof Error ? error.message : String(error));
        }
    }, [api, buildCallbacks, client, options, refreshConversations, setAsking]);
    const rejectFormInteraction = useCallback(async (part) => {
        const interaction = getRequestFormInteraction(part);
        if (!interaction || interaction.status !== "pending")
            return;
        try {
            setSubmittingFormRequestId(interaction.form_request_id);
            const success = await api.submitFormRequest({
                chatId: part.history_id,
                formRequestId: interaction.form_request_id,
                conversationGroupId: part.conversation_group_id,
                rejected: true,
            });
            if (success) {
                setSubmittingFormRequestId(null);
                await refreshConversations();
            }
            else {
                setSubmittingFormRequestId(null);
            }
        }
        catch (error) {
            setSubmittingFormRequestId(null);
            options.onError?.(ConversationErrorType.SSEError, error instanceof Error ? error.message : String(error));
        }
    }, [api, options, refreshConversations]);
    const changeSettings = useCallback((settings) => {
        setContext((prev) => {
            const next = prev
                ? { ...prev, settings: { ...prev.settings, ...settings } }
                : prev;
            contextRef.current = next;
            return next;
        });
    }, []);
    const setConversationContext = useCallback((next) => {
        setContext((prev) => {
            const resolved = typeof next === "function" ? next(prev) : next;
            contextRef.current = resolved;
            return resolved;
        });
    }, []);
    const setHistoryInfo = useCallback((nextHistory) => {
        historyIdRef.current = nextHistory.id;
        setHistory(nextHistory);
    }, []);
    const editPartContent = useCallback((partId, content) => {
        setParts((prev) => prev.map((part) => part.id === partId
            ? { ...part, content: { ...part.content, content } }
            : part));
    }, []);
    const deleteConversationGroup = useCallback((conversationGroupId) => {
        setParts((prev) => prev.filter((part) => part.conversation_group_id !== conversationGroupId));
    }, []);
    const setConversationScore = useCallback((_conversation, _score) => undefined, []);
    const clearPendingInteraction = useCallback(() => {
        setSubmittingFormRequestId(null);
        setPendingPayment(null);
    }, []);
    return {
        context,
        history,
        askingStatus,
        isAsking: askingStatus === AskStatus.Answering,
        parts,
        replyContextAnchor,
        loadConversationsError,
        conversations: [],
        groupedConversations: [],
        initConversation,
        loadConversations,
        refreshConversations,
        changeSettings,
        deleteConversationGroup,
        editPartContent,
        setConversationScore,
        submitFeedback,
        sendMessage,
        regenerate,
        cancel,
        cancelHistoryAnalysis,
        pendingPayment,
        isHitlPending: pendingPayment != null,
        formSubmitting: submittingFormRequestId != null,
        submittingFormRequestId,
        submitFormInteraction,
        rejectFormInteraction,
        clearPendingInteraction,
        setHistory,
        setContext: setConversationContext,
        setConversations: (_next) => undefined,
        setAskingStatus: setAsking,
        setHistoryInfo,
    };
};
export default useChatSessionV2;
//# sourceMappingURL=useChatSessionV2.js.map